import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import Container from 'src/components/shared/Container';
import Seo from 'src/components/Seo';
import seoConfig from '../seo.json';
import DateFilter from 'src/components/user-dashboard/DateFilter';
import DashboardStats from 'src/components/user-dashboard/DashboardStats';
import RejectedSection from 'src/components/user-dashboard/RejectedSection';
import { useGetUserDashboard } from 'src/hooks/application.hooks';
import moment from 'moment';
import NewCreatingPositionLoader from 'src/components/position/NewCreatingPositionLoader';

export type DateFilterType = 'overall' | '7days' | '30days' | 'custom';

export interface DateRange {
    startDate: Date | null;
    endDate: Date | null;
}

const UserDashboard = () => {
    const seoInfo = seoConfig['user-dashboard'] || { title: 'User Dashboard', description: 'User Dashboard' };
    const [dateFilter, setDateFilter] = useState<DateFilterType>('overall');
    const [customDateRange, setCustomDateRange] = useState<DateRange>({
        startDate: null,
        endDate: null,
    });
    const [rejectedSearch, setRejectedSearch] = useState('');
    const [rejectedPage, setRejectedPage] = useState(1);
    const rejectedPageSize = 5;
    // Track the last user-initiated page change to prevent unwanted resets
    const lastUserPageChange = useRef<number>(1);
    // Track if we're currently syncing from server to prevent loops
    const isSyncingFromServer = useRef<boolean>(false);

    const getDateRange = (): DateRange => {
        switch (dateFilter) {
            case '7days':
                return {
                    startDate: moment().subtract(7, 'days').startOf('day').toDate(),
                    endDate: moment().endOf('day').toDate(),
                };
            case '30days':
                return {
                    startDate: moment().subtract(30, 'days').startOf('day').toDate(),
                    endDate: moment().endOf('day').toDate(),
                };
            case 'custom':
                return customDateRange;
            default:
                return {
                    startDate: null,
                    endDate: null,
                };
        }
    };

    const apiParams = useMemo(() => {
        // CRITICAL: Use lastUserPageChange if it differs from rejectedPage
        // This prevents race conditions where rejectedPage might temporarily revert
        // to an old value due to React's state batching or re-renders
        const pageToUse = lastUserPageChange.current !== rejectedPage
            ? lastUserPageChange.current
            : rejectedPage;

        const dateRange = getDateRange();
        const params: {
            fromDate?: string;
            toDate?: string;
            search?: string;
            page: number;
            limit: number;
        } = {
            page: pageToUse,
            limit: rejectedPageSize,
        };

        if (dateRange.startDate && dateRange.endDate) {
            params.fromDate = moment(dateRange.startDate).format('YYYY-MM-DD');
            params.toDate = moment(dateRange.endDate).format('YYYY-MM-DD');
        }

        if (rejectedSearch) {
            params.search = rejectedSearch;
        }

        // console.log('[Frontend] apiParams memoized:', {
        //     params,
        //     rejectedPage,
        //     lastUserPageChange: lastUserPageChange.current,
        //     pageToUse,
        //     dependencies: { dateFilter, customDateRange, rejectedSearch, rejectedPage, rejectedPageSize },
        //     timestamp: new Date().toISOString(),
        // });

        return params;
    }, [dateFilter, customDateRange, rejectedSearch, rejectedPage, rejectedPageSize]);

    const { data: dashboardData, isLoading, isFetching } = useGetUserDashboard(apiParams);

    const handleDateFilterChange = (filter: DateFilterType) => {
        setDateFilter(filter);
        setRejectedPage(1); // Reset to first page when date filter changes
    };

    const handleCustomDateRangeChange = (range: DateRange) => {
        setCustomDateRange(range);
        setRejectedPage(1); // Reset to first page when date range changes
    };
    const handleRejectedSearch = useCallback((value: string) => {
        setRejectedSearch(value);
        setRejectedPage(1);
    }, []);
    const handleRejectedPageChange = useCallback((page: number) => {
        // CRITICAL: Update the ref FIRST before state update
        // This ensures apiParams will use the correct page even if state update is delayed
        lastUserPageChange.current = page;
        isSyncingFromServer.current = false;
        // Use functional update to ensure we're using the latest state
        setRejectedPage(prevPage => {
            if (prevPage !== page) {
                console.log('[Frontend] Setting rejectedPage:', { from: prevPage, to: page });
            }
            return page;
        });
    }, [rejectedPage]);

    // Keep client page state in sync with server meta ONLY if server corrects an invalid page
    // Don't sync if user intentionally changed the page
    useEffect(() => {
        // Skip if we're already syncing to prevent loops
        if (isSyncingFromServer.current) {
            return;
        }

        const serverPage = dashboardData?.data?.rejectedMeta?.page;
        const serverTotalPages = dashboardData?.data?.rejectedMeta?.totalPages;

        // If we don't have server data yet, don't do anything
        if (typeof serverPage !== 'number' || typeof serverTotalPages !== 'number') {
            return;
        }

        console.log('[Frontend] Page sync check:', {
            serverPage,
            clientPage: rejectedPage,
            lastUserPageChange: lastUserPageChange.current,
            serverTotalPages,
            timestamp: new Date().toISOString(),
        });

        // CRITICAL: Only process server responses that match the user's last action
        // This prevents stale responses from old queries (e.g., page 1) from overriding
        // new user actions (e.g., page 2)
        const isResponseToUserAction = serverPage === lastUserPageChange.current;

        // If this is NOT a response to the user's action, it's a stale response - IGNORE IT COMPLETELY
        if (!isResponseToUserAction) {
            // CRITICAL: If rejectedPage was incorrectly reset, restore it from lastUserPageChange
            if (rejectedPage !== lastUserPageChange.current && lastUserPageChange.current >= 1 && lastUserPageChange.current <= serverTotalPages) {
                isSyncingFromServer.current = true;
                setRejectedPage(lastUserPageChange.current);
                setTimeout(() => {
                    isSyncingFromServer.current = false;
                }, 0);
            }
            return;
        }

        // If we get here, this IS a response to the user's action
        // Only sync if server page doesn't match client page
        // This handles cases where server corrected the page (e.g., requested page 10, got page 3)
        if (serverPage !== rejectedPage) {
            // Check if server corrected an out-of-range page
            const wasOutOfRange = rejectedPage > serverTotalPages || rejectedPage < 1;
            isSyncingFromServer.current = true;
            lastUserPageChange.current = serverPage; // Update ref to match
            setRejectedPage(serverPage);
            // Reset the sync flag after state update
            setTimeout(() => {
                isSyncingFromServer.current = false;
            }, 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashboardData?.data?.rejectedMeta?.page, dashboardData?.data?.rejectedMeta?.totalPages]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <NewCreatingPositionLoader showAnimation={true} progressBar={false} customClassCss="!w-[20%] mx-auto" />
            </div>
        );
    }

    return (
        <div>
            <Seo title={seoInfo.title} description={seoInfo.description} />
            <Container className="w-[92%] max-w-[1280px] pt-2 md:pb-2 pb-10 sm:w-[96%] font-inter">
                <div className="md:mt-6 mt-4">
                    {/* Header with Title and Date Filter */}
                    <div className="flex justify-between items-center mb-6">
                        <h1 className="text-2xl font-inter font-semibold dark:text-white text-[#28293D]">User Dashboard</h1>
                        <DateFilter
                            selectedFilter={dateFilter}
                            onFilterChange={handleDateFilterChange}
                            customDateRange={customDateRange}
                            onCustomDateRangeChange={handleCustomDateRangeChange}
                            onApply={() => {
                                // Filter is applied when onFilterChange is called in DateFilter
                            }}
                        />
                    </div>

                    {/* Dashboard Stats */}
                    <div className="mb-8">
                        <DashboardStats dashboardData={dashboardData?.data} />
                    </div>

                    {/* Rejected Section */}
                    <div>
                        <RejectedSection
                            rejectedData={dashboardData?.data?.rejected || []}
                            rejectedMeta={dashboardData?.data?.rejectedMeta}
                            currentPage={rejectedPage}
                            isLoading={isFetching}
                            controlledSearch={rejectedSearch}
                            onSearchChange={handleRejectedSearch}
                            onPageChange={handleRejectedPageChange}
                        />
                    </div>
                </div>
            </Container>
        </div>
    );
};

export default UserDashboard;

